import * as autobind from 'autobind';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { Modal, Button, Form, Container, TextArea, Dropdown, Dimmer, Loader, Message, Icon } from 'semantic-ui-react';
import MRClusterEditor from 'widgets/bussiness/mrcluster-editor';
import EquipmentTypeEditor from 'widgets/bussiness/equipment-type-editor';
import LanguageEditor from 'widgets/bussiness/language-editor';
import SkillsEditor, { SkillType } from 'widgets/bussiness/skills-editor';
import {
  ChangeTnaQuestionBankStore,
  ChangeTnaQuestionBankDto,
  TnaQuestionBankDto,
  ChangeQuestionDto,
  QuestionDto
} from 'stores/assessments/questionBank/tna-questionBank-store';
import { LanguagesStore } from 'stores/configuration/locations/languages-store';
import { nameof } from 'utils/object';
import TestCategorySelector from 'widgets/bussiness/selectors/test-category-selector';
import { ItemReference } from 'stores/dataStore';
import { isNullOrWhiteSpaces } from 'utils/useful-functions';
import MultiProfessionEditor from 'widgets/bussiness/multi-profession-editor';
import i18n from 'i18n';
import OemEditor from 'widgets/bussiness/oem-editor';
import LocationEditorWithRoleLocations from 'widgets/bussiness/location-editor-with-rolelocations';
import { resolve } from 'inversify-react';
import { IdentityService } from 'services/identity-service';

interface ChangeTnaQuestionBankViewProps extends WithTranslation {
  onClose: (isSuccess: boolean) => void;
  changeQuestionBank?: ChangeTnaQuestionBankStore;
  languagesStore?: LanguagesStore;
  currentData: TnaQuestionBankDto;
}

interface ChangeTnaQuestionBankState {
  category: ItemReference;
  skill: ItemReference;
  userClickedSave: boolean;
  questionTranslations: ChangeQuestionDto[];
}

@connect(['changeQuestionBank', ChangeTnaQuestionBankStore], ['languagesStore', LanguagesStore])
class ChangeTnaQuestionBankView extends React.Component<ChangeTnaQuestionBankViewProps, ChangeTnaQuestionBankState> {
  private get changeQuestionBankStore() {
    return this.props.changeQuestionBank;
  }

  @resolve(IdentityService)
  private identityService: IdentityService;

  constructor(props: ChangeTnaQuestionBankViewProps) {
    super(props);

    this.state = {
      category: Object.assign({ id: this.props.currentData.testCategoryId, title: this.props.currentData.testCategory.name }),
      skill: Object.assign(
        this.props.currentData.tnaSkill
          ? { id: this.props.currentData.tnaSkillId, title: this.props.currentData.tnaSkill.name }
          : { id: null, title: null }
      ),
      userClickedSave: false,
      questionTranslations: this.props.currentData.questionTranslations
        ? this.mapToCreateQuestionDto(this.props.currentData.questionTranslations)
        : []
    };
    this.changeQuestionBankStore.state.result = null;

    this.changeQuestionBankStore.change({
      id: this.props.currentData.id,
      question: { text: this.props.currentData.question.text, languageId: this.props.currentData.question.language.id },
      testCategoryId: this.props.currentData.testCategoryId,
      clusterId: this.props.currentData.equipmentType ? this.props.currentData.equipmentType.clusterId : null,
      professions: this.props.currentData.professions ? this.props.currentData.professions.map(p => p.id) : [],
      equipmentTypeId: this.props.currentData.equipmentTypeId,
      oemId: this.props.currentData.oemId,
      tnaSkillId: this.props.currentData.tnaSkillId,
      questionTranslations: this.props.currentData.questionTranslations
        ? this.mapToCreateQuestionDto(this.props.currentData.questionTranslations)
        : [],
      isMachineRelated: this.props.currentData.isMachineRelated,
      locationId: this.props.currentData.locationId,
      locationRequired: false
    });
  }
  componentDidMount() {
    const change = {};
    change['locationRequired'] = !!this.props.currentData.locationId;
    this.changeQuestionBankStore.change({ ...this.changeQuestionBankStore.state.item, ...change });
  }
  UNSAFE_componentWillReceiveProps(nextProps: ChangeTnaQuestionBankViewProps) {
    if (this.changeQuestionBankStore.state.result && this.changeQuestionBankStore.state.result.isSuccess) {
      nextProps.onClose(true);
    }
  }

  private handleValue = (property: keyof ChangeTnaQuestionBankDto, value: any) => {
    this.setState({ userClickedSave: false });
    const change = {};
    if (property === nameof<ChangeTnaQuestionBankDto>('tnaSkillId') && value != null) {
      if (!isNullOrWhiteSpaces(value.id)) {
        change['tnaSkillId'] = value.id;
        this.changeQuestionBankStore.change({ ...this.changeQuestionBankStore.state.item, ...change });
        if (this.changeQuestionBankStore.state.item.isMachineRelated !== value.isMachineRelated)
          this.onChangeIsMachineRelated(!!value.isMachineRelated);
      } else {
        change[property] = null;
        this.changeQuestionBankStore.change({ ...this.changeQuestionBankStore.state.item, ...change });
      }
    } else {
      change[property] = value;
      this.changeQuestionBankStore.change({ ...this.changeQuestionBankStore.state.item, ...change });
    }
  };

  private mapToCreateQuestionDto(items: QuestionDto[]): ChangeQuestionDto[] {
    let data: ChangeQuestionDto[] = [];

    items.forEach(i => {
      data.push({ text: i.text, languageId: i.language.id });
    });
    return data;
  }

  private onChangeQuestion = (q: string) => {
    let question = { ...this.changeQuestionBankStore.state.item.question };
    if (question) question.text = q;
    else question = { text: q, languageId: this.props.currentData.question.language.id };

    this.handleValue('question', question);
  };

  @autobind
  private onChangeItem() {
    this.setState({ userClickedSave: true });
    this.changeQuestionBankStore.update();
  }

  @autobind
  private onCancelNewItem() {
    this.changeQuestionBankStore.clear();
    this.props.onClose(false);
  }

  addQuestionsTranslation = () => {
    let question = this.state.questionTranslations;
    question.push({ text: '', languageId: null });

    this.setState({ questionTranslations: question });
  };

  handleQuestionTransaltionTextChange = (index: number, questionText: string) => {
    let questionTranslations = this.state.questionTranslations;
    let question = questionTranslations[index];
    question.text = questionText;

    this.setState({ questionTranslations }, () =>
      this.handleValue(nameof<ChangeTnaQuestionBankDto>('questionTranslations'), questionTranslations)
    );
  };

  handleQuestionTransaltionLanguageChange = (index: number, languageId: string) => {
    let questionTranslations = this.state.questionTranslations;
    let question = questionTranslations[index];
    question.languageId = languageId;
    this.setState({ questionTranslations }, () =>
      this.handleValue(nameof<ChangeTnaQuestionBankDto>('questionTranslations'), questionTranslations)
    );
  };

  removeDateRange = index => {
    let questionTranslations = this.state.questionTranslations;
    questionTranslations.splice(index, 1);

    this.setState({ questionTranslations }, () =>
      this.handleValue(nameof<ChangeTnaQuestionBankDto>('questionTranslations'), questionTranslations)
    );
  };

  showQuestionTranslationRows = () => {
    return this.state.questionTranslations.map((question, index) => {
      return (
        <Form.Group className="tna-question-bank__new-rows" key={`${question.languageId}_${index}`}>
          <Form.Field
            required
            className="tna-question-bank__label-textarea"
            error={this.state.userClickedSave && question.text.length === 0}
          >
            <TextArea
              maxLength="500"
              className="tna-question-bank__new-rows__textarea"
              value={question ? question.text : ''}
              onChange={(e, { value }) => this.handleQuestionTransaltionTextChange(index, value.toString())}
            />
          </Form.Field>
          <div className="tna-question-bank__new-rows__drop-remove">
            <LanguageEditor
              className={'planit-user-dropdown-custom'}
              nullable
              value={question.languageId}
              onChange={c => {
                this.handleQuestionTransaltionLanguageChange(index, c == null ? null : c.id);
              }}
              onlyForTests={true}
            />

            <div className="new-pattern__remove-icon">
              <Icon className="clickable-icon remove-icon" color="red" name="remove" onClick={() => this.removeDateRange(index)} />
            </div>
          </div>
        </Form.Group>
      );
    });
  };

  private onChangeIsMachineRelated(value: boolean) {
    this.setState({ userClickedSave: false });
    const change = { isMachineRelated: value };
    if (!value) {
      change['clusterId'] = null;
      change['equipmentTypeId'] = null;
      change['oemId'] = null;
    }

    this.changeQuestionBankStore.change({ ...this.changeQuestionBankStore.state.item, ...change });
  }

  private onChangeCluster(value: ItemReference) {
    this.setState({ userClickedSave: false });
    const change = { clusterId: value ? value.id : null };
    if (value == null) {
      change['equipmentTypeId'] = null;
      change['oemId'] = null;
    }

    this.changeQuestionBankStore.change({ ...this.changeQuestionBankStore.state.item, ...change });
  }

  private onChangeEquipmentTypeChange(value: ItemReference) {
    this.setState({ userClickedSave: false });
    const change = { equipmentTypeId: value ? value.id : null };
    if (value == null) {
      change['equipmentTypeId'] = null;
    }
    this.changeQuestionBankStore.change({ ...this.changeQuestionBankStore.state.item, ...change });
  }

  public render() {
    const { t } = this.props as any;
    const isMaChineRelatedOptions = [
      { key: 1, text: t('Yes'), value: true },
      { key: 2, text: t('No'), value: false }
    ];
    const modalTitle = 'Edit TNA Question';

    const currentUserInfo = this.identityService.getUserInfo();
    const areYouPoc = IdentityService.isPoc(currentUserInfo);
    const isAdminOrPwerInst = IdentityService.isAdminOrPowerInstructor(currentUserInfo);
    const pocLocations = currentUserInfo.locationsByRoles['PoC'] as string[];
    return (
      <Modal className="tna-question-bank__modal" open closeOnEscape={true} onClose={this.onCancelNewItem} closeOnDimmerClick={false}>
        <Dimmer active={this.changeQuestionBankStore.state.isBusy} style={{ background: 'rgba(0, 0, 0, 0.4)' }}>
          <Loader indeterminate>{t('')}</Loader>
        </Dimmer>
        <Modal.Header className="tna-squestion-bank__modal-header">{t(modalTitle)}</Modal.Header>
        <Modal.Content image>
          <Container>
            {this.changeQuestionBankStore.state.result && !this.changeQuestionBankStore.state.result.isSuccess && (
              <Message
                className="error-message__style"
                icon="exclamation circle"
                error
                header={t('An error ocurred')}
                list={this.changeQuestionBankStore.state.result.messages.map(o => o.body)}
              />
            )}
            {this.changeQuestionBankStore.state.item && (
              <Form>
                <div className="tna-question-bank__wrapper scroll">
                  <Form.Group className="tna-question-bank__selctor-group">
                    <Form.Field className="tna-question-bank__label-input">
                      <label>{t('TNA Skill')}</label>
                      <SkillsEditor
                        nullable
                        skillType={SkillType.TNA}
                        className="planit-user-dropdown-custom"
                        placeholder={t('Select TNA Skill')}
                        value={this.state.skill}
                        onChange={skill => this.handleValue(nameof<ChangeTnaQuestionBankDto>('tnaSkillId'), skill)}
                        clearable
                      />
                    </Form.Field>

                    <Form.Field
                      required
                      className="tna-question-bank__label-input"
                      error={
                        (this.changeQuestionBankStore.state.item.testCategoryId == null ||
                          isNullOrWhiteSpaces(this.changeQuestionBankStore.state.item.testCategoryId)) &&
                        this.state.userClickedSave
                      }
                    >
                      <label>{t('Category')}</label>
                      <TestCategorySelector
                        nullable
                        isTnaCategory={true}
                        className="planit-user-dropdown-custom"
                        placeholder={t('Select Category')}
                        value={this.state.category}
                        onChange={category => this.handleValue(nameof<ChangeTnaQuestionBankDto>('testCategoryId'), category?.id)}
                      />
                    </Form.Field>
                  </Form.Group>

                  <Form.Group className="tna-question-bank__selctor-group">
                    <Form.Field
                      required
                      className="tna-question-bank__label-input"
                      error={
                        (this.changeQuestionBankStore.state.item.professions == null ||
                          this.changeQuestionBankStore.state.item.professions.length === 0) &&
                        this.state.userClickedSave
                      }
                    >
                      <label>{t('Roles')}</label>
                      <MultiProfessionEditor
                        className="planit-user-dropdown"
                        placeholder={t('Roles')}
                        value={this.props.currentData.professions ? this.props.currentData.professions.map(p => p.id) : []}
                        onChange={value => this.handleValue(nameof<ChangeTnaQuestionBankDto>('professions'), value)}
                      />
                    </Form.Field>

                    <Form.Field
                      required
                      className="tna-question-bank__label-input"
                      error={this.state.userClickedSave && this.changeQuestionBankStore.state.item.isMachineRelated == null}
                    >
                      <label>{t('Machine Related')}</label>
                      <Dropdown
                        selection
                        value={this.changeQuestionBankStore.state.item.isMachineRelated}
                        multiple={false}
                        className="planit-user-dropdown-custom normal-look-readonly-select"
                        placeholder={t('Select a skill to fill')}
                        options={isMaChineRelatedOptions}
                        onChange={(e, { value }) => this.onChangeIsMachineRelated(!!value)}
                        disabled={!isNullOrWhiteSpaces(this.changeQuestionBankStore.state.item.tnaSkillId)}
                      />
                    </Form.Field>
                  </Form.Group>

                  <Form.Group className="tna-question-bank__selctor-group">
                    <Form.Field
                      className="tna-question-bank__label-input"
                      error={
                        this.changeQuestionBankStore.state.item.isMachineRelated &&
                        !this.changeQuestionBankStore.state.item.clusterId &&
                        this.state.userClickedSave
                      }
                    >
                      <label>{t('Cluster')}</label>
                      <MRClusterEditor
                        placeholder={t('Cluster')}
                        className="planit-user-dropdown-custom"
                        nullable
                        clearable
                        readOnly={!this.changeQuestionBankStore.state.item.isMachineRelated}
                        value={this.changeQuestionBankStore.state.item.clusterId}
                        onChange={c => {
                          this.onChangeCluster(c ? c : null);
                        }}
                      />
                    </Form.Field>

                    <Form.Field
                      className="tna-question-bank__label-input"
                      error={
                        this.changeQuestionBankStore.state.item.isMachineRelated &&
                        !this.changeQuestionBankStore.state.item.equipmentTypeId &&
                        this.state.userClickedSave
                      }
                    >
                      <label>{t('Equipment Type')}</label>
                      <EquipmentTypeEditor
                        placeholder={t('Equipment Type')}
                        className="planit-user-dropdown-custom"
                        nullable
                        readOnly={
                          !this.changeQuestionBankStore.state.item.isMachineRelated || !this.changeQuestionBankStore.state.item.clusterId
                        }
                        clearable
                        clusterId={this.changeQuestionBankStore.state.item.clusterId}
                        value={this.changeQuestionBankStore.state.item.equipmentTypeId}
                        onChange={c => this.onChangeEquipmentTypeChange(c)}
                      />
                    </Form.Field>
                  </Form.Group>

                  <Form.Group className="tna-question-bank__selctor-group">
                    <Form.Field
                      className="tna-question-bank__label-input"
                      error={
                        this.changeQuestionBankStore.state.item.isMachineRelated &&
                        !this.changeQuestionBankStore.state.item.oemId &&
                        this.state.userClickedSave
                      }
                    >
                      <label>{t('OEM')}</label>
                      <OemEditor
                        placeholder={t('OEM')}
                        className="planit-user-dropdown-custom"
                        nullable
                        equipmentId={this.changeQuestionBankStore.state.item.equipmentTypeId}
                        clearable
                        readonly={
                          !this.changeQuestionBankStore.state.item.isMachineRelated ||
                          !this.changeQuestionBankStore.state.item.clusterId ||
                          !this.changeQuestionBankStore.state.item.equipmentTypeId
                        }
                        value={this.changeQuestionBankStore.state.item.oemId}
                        onChange={c => this.handleValue(nameof<ChangeTnaQuestionBankDto>('oemId'), c ? c.id : null)}
                      />
                    </Form.Field>
                    {(areYouPoc || isAdminOrPwerInst) && this.changeQuestionBankStore.state.item.locationRequired && (
                      <Form.Field
                        key={'location-editor-field-edit-tna'}
                        className="tna-question-bank__label-input"
                        required
                        error={this.changeQuestionBankStore.state.result && !this.changeQuestionBankStore.state.item.locationId}
                      >
                        <label>{t('Location')}</label>
                        <LocationEditorWithRoleLocations
                          key={'location-editor-edit-tna'}
                          className="planit-user-dropdown-custom"
                          value={this.changeQuestionBankStore.state.item.locationId}
                          locationsReceived={areYouPoc ? pocLocations : []}
                          onChange={location =>
                            this.handleValue(
                              nameof<ChangeTnaQuestionBankDto>('locationId'),
                              location ? (location.id ? location.id : location) : null
                            )
                          }
                          skipDefaultClass
                          customPlaceholder={"PoC's Location"}
                        />
                      </Form.Field>
                    )}
                  </Form.Group>
                  <Form.Group className="tna-question-bank__selctor-textarea">
                    <Form.Field
                      required
                      className="tna-question-bank__label-textarea"
                      error={this.state.userClickedSave && this.changeQuestionBankStore.state.item.question.text.length === 0}
                    >
                      <label>{t('Question')}</label>
                      <TextArea
                        maxLength="500"
                        className="tna-question-bank__custom-editorequest__textarea"
                        value={this.changeQuestionBankStore.state.item.question?.text || ''}
                        onChange={(e, { value }) => this.onChangeQuestion(value.toString())}
                      />

                      <Button
                        className="custom-table-add-row-btn add-row-btn"
                        type="button"
                        content="Add Row"
                        onClick={this.addQuestionsTranslation}
                      >
                        {i18n.t('Add Translation')}
                      </Button>
                    </Form.Field>
                  </Form.Group>
                  {this.state.questionTranslations.length > 0 && <>{this.showQuestionTranslationRows()}</>}
                </div>
              </Form>
            )}
          </Container>
        </Modal.Content>
        <Modal.Actions className="new-tna-question-bank__btns">
          <Button onClick={this.onCancelNewItem} basic>
            {t('Cancel')}
          </Button>
          <Button onClick={this.onChangeItem} positive>
            {t('Save')}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

// Wire up the React component to the Redux store
export default withTranslation()(ChangeTnaQuestionBankView);
